import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';
import StatusBadge from '../components/StatusBadge';

export default function Dashboard() {
  const { authFetch, user } = useAuth();
  const { tier, getCapStatus } = useSubscription();
  const navigate = useNavigate();
  const [data, setData] = useState({ bookings: [], proposals: [], inquiries: [], appointments: [], autoDrafts: [] });
  const [aiUsage, setAiUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [bookingsRes, proposalsRes, faqRes, draftsRes, aiUsageRes] = await Promise.allSettled([
          authFetch('/api/bookings'),
          authFetch('/api/proposals'),
          authFetch('/api/faq/inquiries'),
          authFetch('/api/auto-emails'),
          authFetch('/api/account/ai-usage'),
        ]);
        const safeJson = async (res) => {
          try { return await res.json(); } catch { return null; }
        };
        const bookings = bookingsRes.status === 'fulfilled' ? await safeJson(bookingsRes.value) : null;
        const proposals = proposalsRes.status === 'fulfilled' ? await safeJson(proposalsRes.value) : null;
        const faqData = faqRes.status === 'fulfilled' ? await safeJson(faqRes.value) : null;
        const autoDrafts = draftsRes.status === 'fulfilled' ? await safeJson(draftsRes.value) : null;
        const aiUsageData = aiUsageRes.status === 'fulfilled' ? await safeJson(aiUsageRes.value) : null;
        setData({
          bookings: Array.isArray(bookings) ? bookings : [],
          proposals: Array.isArray(proposals) ? proposals : [],
          inquiries: Array.isArray(faqData) ? faqData : [],
          appointments: [],
          autoDrafts: Array.isArray(autoDrafts) ? autoDrafts : [],
        });
        setAiUsage(aiUsageData?.count ?? 0);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Goedemorgen' : hour < 18 ? 'Goedemiddag' : 'Goedenavond';
  const agentName = user?.agent_name || '';

  const pendingInquiries = data.inquiries.filter(i => i.status !== 'replied');
  const pendingDrafts = data.autoDrafts.filter(d => d.status === 'pending');
  const recentProposals = data.proposals.slice(0, 4);
  const recentBookings = data.bookings.slice(0, 4);

  const stats = [
    { label: 'Boekingen', value: data.bookings.length, sub: 'totaal', subColor: 'var(--color-text-tertiary)' },
    { label: 'Open vragen', value: pendingInquiries.length, sub: pendingInquiries.length > 0 ? 'wacht op jou' : 'alles beantwoord', subColor: pendingInquiries.length > 0 ? 'var(--color-warning)' : 'var(--color-success)' },
    { label: 'AI Gebruik', value: aiUsage ?? '—', sub: 'acties deze maand', subColor: 'var(--color-text-tertiary)' },
    { label: 'E-mail concepten', value: pendingDrafts.length, sub: pendingDrafts.length > 0 ? 'wacht op review' : 'alles afgerond', subColor: pendingDrafts.length > 0 ? 'var(--color-warning)' : 'var(--color-success)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PageHeader
        title={`${greeting}${agentName ? `, ${agentName}` : ''}`}
        subtitle={new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
        actions={
          <Button variant="primary" onClick={() => navigate('/proposals')}>
            + Nieuwe offerte
          </Button>
        }
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 16 }}>
          {stats.map(s => (
            <div key={s.label} style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 14px',
            }}>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--color-text-primary)', lineHeight: 1 }}>
                {loading ? '—' : s.value}
              </div>
              <div style={{ fontSize: 10, color: s.subColor, marginTop: 4 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Usage card — only for assistent tier */}
        {tier === 'assistent' && (
          <div style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: '14px 16px',
            marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Gebruik deze maand</span>
              {(getCapStatus('proposals').percentage >= 60 || getCapStatus('faq_replies').percentage >= 60) && (
                <a href="/account" style={{ fontSize: 11, color: 'var(--brand)', textDecoration: 'underline' }}>
                  Upgrade naar Bureau
                </a>
              )}
            </div>
            {['proposals', 'faq_replies'].map(feature => {
              const capInfo = getCapStatus(feature);
              const label = feature === 'proposals' ? 'Reisvoorstellen' : 'FAQ antwoorden';
              const barColor = capInfo.percentage >= 100 ? '#ef4444' : capInfo.percentage >= 60 ? '#f59e0b' : 'var(--brand)';
              return (
                <div key={feature} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{label}</span>
                    <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{capInfo.used ?? 0} / {capInfo.cap}</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(capInfo.percentage, 100)}%`, background: barColor, borderRadius: 2, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Two column */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Inbox */}
          <PanelCard title="FAQ Inbox" action="Alle bekijken" onAction={() => navigate('/faq')}>
            {pendingInquiries.length === 0 ? (
              <EmptyRow text="Geen openstaande vragen ✓" />
            ) : pendingInquiries.slice(0, 4).map(inq => (
              <ListRow
                key={inq.id}
                title={inq.sender_name || inq.sender_email || 'Onbekende afzender'}
                sub={inq.subject || 'Geen onderwerp'}
                status="new"
                onClick={() => navigate('/faq')}
              />
            ))}
          </PanelCard>

          {/* Auto-email drafts */}
          <PanelCard title="E-mail concepten" action="Bekijken" onAction={() => navigate('/faq')}>
            {pendingDrafts.length === 0 ? (
              <EmptyRow text="Geen openstaande e-mail concepten ✓" />
            ) : pendingDrafts.slice(0, 4).map(d => (
              <ListRow
                key={d.id}
                title={`${d.type === 'pre_trip' ? 'Pre-trip' : 'Post-trip'} e-mail klaar`}
                sub={d.subject || 'AI concept wacht op jouw goedkeuring'}
                status="draft"
                onClick={() => navigate('/faq')}
              />
            ))}
          </PanelCard>

          {/* Recent proposals */}
          <PanelCard title="Recente reisvoorstellen" action="Alle reisvoorstellen" onAction={() => navigate('/proposals')}>
            {recentProposals.length === 0 ? (
              <EmptyRow text="Nog geen offertes" />
            ) : recentProposals.map(p => (
              <ListRow
                key={p.id}
                title={`${p.destination || 'Bestemming'} — ${p.client_name || 'Klant'}`}
                sub={p.created_at ? new Date(p.created_at).toLocaleDateString('nl-NL') : ''}
                status={p.status || 'concept'}
                onClick={() => navigate('/proposals')}
              />
            ))}
          </PanelCard>

          {/* Recent bookings */}
          <PanelCard title="Recente boekingen" action="Alle boekingen" onAction={() => navigate('/bookings')}>
            {recentBookings.length === 0 ? (
              <EmptyRow text="Nog geen boekingen geüpload" />
            ) : recentBookings.map(b => (
              <ListRow
                key={b.id}
                title={b.destination || b.hotel_name || 'Boeking'}
                sub={b.client_name || b.departure_date || ''}
                status={b.enrichment_status === 'done' ? 'done' : 'pending'}
                onClick={() => navigate('/bookings')}
              />
            ))}
          </PanelCard>
        </div>

        {/* AI suggestion strip */}
        {pendingDrafts.length > 0 && (
          <div style={{
            marginTop: 12,
            background: 'var(--color-accent-light)',
            border: '1px solid rgba(26,110,245,0.15)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 14 }}>✦</span>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>
              <strong style={{ color: 'var(--color-text-primary)' }}>
                {pendingDrafts.length} automatische e-mail{pendingDrafts.length > 1 ? 's' : ''}
              </strong>
              {' '}staan klaar als concept — controleer en verstuur via Inbox.
            </div>
            <button
              onClick={() => navigate('/faq')}
              style={{ fontSize: 12, color: 'var(--color-accent-text)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
            >
              Bekijken →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PanelCard({ title, action, onAction, children }) {
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, fontSize: 12, fontWeight: 500 }}>
        <span style={{ color: 'var(--color-text-primary)' }}>{title}</span>
        {action && (
          <button onClick={onAction} style={{ fontSize: 11, color: 'var(--color-accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}>
            {action}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ text }) {
  return (
    <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12 }}>
      {text}
    </div>
  );
}

function ListRow({ title, sub, status, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 0',
        borderBottom: '1px solid var(--color-border)',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </div>
        {sub && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 1 }}>{sub}</div>}
      </div>
      <StatusBadge status={status} />
    </div>
  );
}
